import type { IDeferred, IQService } from 'angular';
import type { ViewParameters } from './model/view-parameters';
import { ContentWidgetType, ContentWidgetViewType } from '../../model/enumerations';
import { ContentWidgetService, WidgetData } from '../../services/content-widget-service';
import type { I18n } from '../../../../i18n/mx-internationalization';
import type { ColumnDefinitionService } from '../../services/column-definition-service';
import type { SchemaService } from '../../services/schema-service';
import { DataModelSystemProperty } from '../../model/data-model/data-model-calculated-property';
import type { PersonalizationService } from '../../../../App/services/personalization-service';
import type { DataModelService } from '../../services/data-model-service';
import type { MxContextService } from '../../../Pandora/services/mx-context-service';
import type { MxActionManagerService } from '../../../Pandora/services/mx-action-manager-service';
import type { AgentSearchContext } from '../../model/search-context';
import type { DataQuery } from '../../modules/DataQueryModel/contracts';

export interface ViewInfo {
	Type: number;
	Title: string;
	Template?: string;
}

export interface ViewParametersResult {
	viewParameters: ViewParameters;
	viewInfos: ViewInfo[];
	defaultView?: ViewInfo;
	customFormView?: string;
	customFormServiceContextInfo?: {
		widgetId: string;
		viewName: string;
	};
	freeDataSourceDefer: IDeferred<unknown>;
}

export const AVAILABLE_ROW_HEIGHTS = [
	{
		size: 40,
		key: 'solutionBuilderAgent.common.comfortable',
		title: 'Comfortable',
	},
	{
		size: 30,
		key: 'solutionBuilderAgent.common.standard',
		title: 'Standard',
	},
	{
		size: 22,
		key: 'solutionBuilderAgent.common.compact',
		title: 'Compact',
	},
];

export const DEFAULT_ROW_HEIGHT = AVAILABLE_ROW_HEIGHTS[0].size;

export class SearchPageHelperService {
	static readonly $inject = [
		'$q',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.internationalization',
		'mx.SolutionBuilderAgent.mxColumnDefinitionService',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.SolutionBuilderAgent.DataModelService',
		'mx.shell.ContextService',
		'mx.shell.ActionManagerService',
	];

	constructor(
		private readonly $q: IQService,
		private readonly contentWidgetService: ContentWidgetService,
		private readonly i18n: I18n,
		private readonly columnDefinition: ReturnType<typeof ColumnDefinitionService>,
		private readonly schemaService: SchemaService,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly dataModelService: ReturnType<typeof DataModelService>,
		private readonly contextService: ReturnType<typeof MxContextService>,
		private readonly actionManagerService: MxActionManagerService
	) {
		AVAILABLE_ROW_HEIGHTS.forEach((rowHeight) => {
			rowHeight.title = i18n.get(rowHeight.key) ?? rowHeight.title;
		});
	}

	public getEmptyGridWidget(dataQueryId: string, entities: WidgetData['Entities']): WidgetData {
		return {
			Id: '',
			Name: '',
			EntityType: '',
			Entity: '',
			Entities: entities,
			CustomEntities: true,
			Tag: '',
			WidgetType: ContentWidgetType.List,
			Applications: '',
			HelpValue: '',
			DataQueryId: dataQueryId,
			GridViewEnabled: true,
			DefaultView: ContentWidgetViewType.Grid,
			Template: {
				Id: '',
				Views: [],
				DataModelExtensions: '',
				NonVisualTemplate: '',
			},
		};
	}

	async getSearchPageParams(widget: WidgetData, context: AgentSearchContext) {
		const viewInfos = this.getViewInfos(widget);
		// Gets an info about the default view in case it wasn't set before.
		const defaultViewType = this.contentWidgetService.getDefaultViewType(widget.Id) ?? widget.DefaultView;
		const defaultView = viewInfos.find((view) => view.Type === defaultViewType);
		const customFormViewObject = widget.Template.Views?.find((view) => view.Type === ContentWidgetViewType.CustomSearchForm);
		const customFormView = customFormViewObject?.Template ?? '';
		const customFormServiceContextInfo = {
			widgetId: widget.Id,
			viewName: customFormViewObject?.Key ?? '',
		};

		const freeDataSourceDefer = this.$q.defer();
		const dataQuery = await this.columnDefinition.getGridDefinitionById(widget.DataQueryId, widget.Id);
		const entities = await this.getWidgetEntities(widget, dataQuery);
		const isExportSupported = widget.Id && (!dataQuery.ServiceInfo || dataQuery.isExportSupported());

		const viewParameters: ViewParameters = {
			isSearchPage: true,
			context: context,
			filterExpression: widget.FilterExpression,
			isMultiFragment: widget.IsMultiFragment,
			dataQueryId: widget.DataQueryId,
			nonVisualTemplate: widget.Template.NonVisualTemplate,
			id: widget.Id,
			name: widget.Name,
			templateId: widget.Template.Id,
			customEntities: widget.CustomEntities,
			entities,
			quickFilters: Boolean(widget.RedFilter || widget.YellowFilter || widget.GreenFilter),
			redFilter: widget.RedFilter,
			yellowFilter: widget.YellowFilter,
			greenFilter: widget.GreenFilter,
			quickFilterTitles: {
				red: widget.RedTitle,
				yellow: widget.YellowTitle,
				green: widget.GreenTitle,
			},
			loadOnlyFilteredData: !!widget.LoadOnlyFilteredData,
			calendar: {
				titleKey: widget.CalendarSubjectField || '_displayName',
				descriptionKey: widget.CalendarDescriptionField,
				startDateKey: widget.CalendarStartDateField,
				defaultView: widget.CalendarDefaultView,
				endDateKey: widget.CalendarEndDateField,
				wholeDayKey: widget.CalendarEventIsWholeDayColumn,
				readonlyKey: widget.CalendarEventIsReadOnlyColumn,
				backgroundKey: widget.CalendarEventIsBackgroundColumn,
				showHolidays: widget.CalendarShowHolidays,
				groupByExtraDimension: widget.GroupByExtraDimension,
				calendarExtraDimensionRelation: widget.CalendarExtraDimensionRelation,
				calendarExtraDimensionImageColumn: widget.CalendarExtraDimensionImageColumn,
				calendarItemColorColumn: widget.CalendarItemColorColumn,
				showDayView: Boolean(widget.ShowDayView),
				showWeekView: Boolean(widget.ShowWeekView),
				showMonthView: Boolean(widget.ShowMonthView),
			},
			board: {
				defaultHorizontalGroupingField: widget.DefaultBoardGroupingAttribute,
				dragAndDropEnabled: widget.BoardDragAndDropEnabled,
			},
			showArchivedDataToggle: Boolean(widget.ShowArchivedDataToggle),

			service: dataQuery.Service,
			serviceInfo: dataQuery.ServiceInfo,
			dataQuery: dataQuery,
			dataModel: await this.getDataModel(widget, context.parameters.navigationId ?? '', freeDataSourceDefer),
			isExportAllAvailable: isExportSupported && (await this.actionManagerService.isExportAllAvailable(entities.map((entity) => entity.name))),
			autoRefreshInGridEnabled: widget.AutoRefreshInGridEnabled ?? false,

			queries: [],
			searchOptions: {
				context: context,
				filters: [],
				structures: [],
				searchForm: undefined,
			},
		};

		await this.initRowHeight(viewParameters);
		await this.initColoring(viewParameters);
		await this.initQuickFilters(viewParameters);

		return { viewParameters, viewInfos, defaultView, customFormView, customFormServiceContextInfo, freeDataSourceDefer };
	}

	private getViewInfos(widget: WidgetData) {
		const i18nSPViews = this.i18n.get('solutionBuilderAgent.layouts.search-page.views');

		let hasBoardView = false;
		const viewInfos: ViewInfo[] =
			widget.Template.Views?.filter((view) => {
				hasBoardView = hasBoardView || (view.Type === ContentWidgetViewType.Board && !!widget.BoardViewEnabled);

				return (
					(view.Type === ContentWidgetViewType.List && widget.ListViewEnabled) ||
					(view.Type === ContentWidgetViewType.Tile && widget.TileViewEnabled) ||
					(view.Type === ContentWidgetViewType.Board && widget.BoardViewEnabled)
				);
			}).map(function (view) {
				return {
					Type: view.Type,
					Title: view.Title,
					Template: view.Template,
				};
			}) ?? [];

		if (widget.CalendarViewEnabled) {
			viewInfos.push({
				Type: ContentWidgetViewType.Calendar,
				Title: i18nSPViews.calendar,
			});
		}

		if (widget.GridViewEnabled) {
			viewInfos.push({
				Type: ContentWidgetViewType.Grid,
				Title: i18nSPViews.grid,
			});
		}

		// If there is no designed Board view but BoardView is enabled than show board with static items
		if (widget.BoardViewEnabled && !hasBoardView) {
			viewInfos.push({
				Type: ContentWidgetViewType.Board,
				Title: i18nSPViews.board,
			});
		}

		return viewInfos;
	}

	private async getDataModel(widget: WidgetData, navigationId: string, freeDataSourceDefer: IDeferred<unknown>) {
		const dataModelExtensions = widget.Template.DataModelExtensions;

		const dataModelOptions = dataModelExtensions
			? JSON.parse(dataModelExtensions)
			: {
					Sources: [],
					Properties: [],
				};

		dataModelOptions.dataQueryId = widget.DataQueryId;
		dataModelOptions.widgetId = widget.Id;

		const ctx = {
			WidgetId: dataModelOptions.widgetId,
			NavigationId: navigationId.toLowerCase(),
		};
		const staticData: { Context: Record<string, unknown> } = { Context: ctx };

		let sharedContext = this.contextService.val('ApplicationSharedContext');
		if (!sharedContext) {
			this.contextService.val('ApplicationSharedContext', (sharedContext = {}));
		}
		staticData.Context[DataModelSystemProperty.Shared] = sharedContext;

		return await this.dataModelService.loadDataModel(dataModelOptions, staticData, undefined, freeDataSourceDefer.promise);
	}

	private async getWidgetEntities(widget: WidgetData, dataQuery: DataQuery) {
		const typesInfo = await Promise.all((widget.Entities ?? []).map((entity) => this.schemaService.getType(entity.Name)));

		return await Promise.all(
			typesInfo.map(async (info, index) => {
				if (!info) {
					if (dataQuery.serviceInfo) {
						const name = 'service_' + dataQuery.ServiceInfo?.Name;
						return {
							id: 'service_' + dataQuery.ServiceInfo?.Id,
							name: name,
							displayName: (await dataQuery.getRecordTypeDisplayString()) || name,
						};
					} else {
						return {
							id: widget.Entities[index].Id || widget.Entities[index].Name,
							name: widget.Entities[index].Name,
							displayName: widget.Entities[index].DisplayName || widget.Entities[index].Name,
						};
					}
				} else {
					return {
						id: info.Id,
						name: info.InternalName,
						displayName: info.DisplayName,
					};
				}
			})
		);
	}

	private async initRowHeight(viewParameters: ViewParameters) {
		let rowHeight = (await this.personalizationService.getRowHeight()) ?? DEFAULT_ROW_HEIGHT;
		Object.defineProperty(viewParameters, 'rowHeight', {
			get: function () {
				return rowHeight;
			},
			set: (newRowHeight) => {
				rowHeight = newRowHeight || DEFAULT_ROW_HEIGHT;
				this.personalizationService.setRowHeight(rowHeight);
			},
		});
	}

	private async initColoring(viewParameters: ViewParameters) {
		let rowColoringEnabled = (await this.personalizationService.getRowsColoring()?.enabled) ?? true;

		Object.defineProperty(viewParameters, 'rowColoringEnabled', {
			get: function () {
				return typeof rowColoringEnabled === 'undefined' ? true : rowColoringEnabled;
			},
			set: (newValue) => {
				rowColoringEnabled = newValue;
				this.personalizationService.setRowsColoringAttribute('rowColoringEnabled', rowColoringEnabled);
			},
		});
	}

	private async initQuickFilters(viewParameters: ViewParameters) {
		let quickFilters = (await this.personalizationService.getQuickFilters(viewParameters.dataQueryId)) ?? [];

		Object.defineProperty(viewParameters, 'quickFilters', {
			get: function () {
				return quickFilters;
			},
			set: (newValue) => {
				quickFilters = newValue;
				this.personalizationService.setQuickFilters(viewParameters.dataQueryId, newValue);
			},
		});
	}

	public async getAutoRefreshConfig(dataQueryId: string, reload: () => void) {
		let _refreshInterval: NodeJS.Timeout | undefined;
		const MIN_REFRESH_INTERVAL = 2;

		const interval: number = +(await this.personalizationService.getRefreshInterval(dataQueryId));

		const autoRefreshConfig = {
			_enable: typeof interval === 'number' && !isNaN(interval) && interval >= MIN_REFRESH_INTERVAL,
			_interval: typeof interval === 'number' && !isNaN(interval) && interval >= MIN_REFRESH_INTERVAL ? interval : MIN_REFRESH_INTERVAL,
			get interval() {
				return this._interval;
			},
			set interval(newInterval) {
				this._interval = !isNaN(newInterval) && newInterval >= MIN_REFRESH_INTERVAL ? newInterval : MIN_REFRESH_INTERVAL;
				updateAutoRefreshSettings();
			},

			set enable(enable) {
				this._enable = enable;
				updateAutoRefreshSettings();
			},
			get enable() {
				return this._enable;
			},

			cancel: () => {
				if (_refreshInterval) {
					clearInterval(_refreshInterval);
				}
			},
		};

		const updateAutoRefreshSettings = () => {
			if (_refreshInterval) {
				clearInterval(_refreshInterval);
			}

			if (autoRefreshConfig.enable && autoRefreshConfig.interval >= MIN_REFRESH_INTERVAL) {
				_refreshInterval = setInterval(reload, autoRefreshConfig.interval * 60000);
			}

			this.personalizationService.saveRefreshInterval(autoRefreshConfig.enable ? autoRefreshConfig.interval : 0, dataQueryId);
		};

		updateAutoRefreshSettings();

		return autoRefreshConfig;
	}
}
